import { SwapOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useState } from "react";
import { $t } from "~/i18n.js";
import { selectElementByID } from "../../lib/helpers.js";
import { SelectPilot } from "../profile/selectPilot.jsx";
import "./FlightBar.css";
import { FlightDetailsModal } from "./sections/flightDetailsModal.jsx";
import { SectionButtons } from "./sections/sectionButtons.jsx";
import { SectionChecks } from "./sections/sectionChecks.jsx";
import { SectionKist } from "./sections/sectionKist.jsx";
import { SectionStartmethode } from "./sections/sectionStartmethode.jsx";
import { SleepActions } from "./sections/sleepActions.jsx";

export const FlightBar = ({
  club,
  isPending,
  readOnlyDay,
  flight,
  allFlights,
  updateFlight,
  sleepFlight,
  disconnectSleepFlight,
  connectSleepFlight,
  addNewSleepFlight,
  kisten,
  sleep_kisten,
  vliegers,
  aanmeldingen,
  setSelectedPilot,
  activeDay,
  enable_authorization = false,
  showFinance = false,
  dayActiveSleepKist = null,
}) => {
  // check if selectedKist already exists, required for kist and tweede_inzittende registration
  const selectedKist = selectElementByID(
    kisten,
    "registratie",
    flight && flight.registratie,
  );

  const selectedSleepKist = selectElementByID(
    sleep_kisten,
    "registratie",
    sleepFlight && sleepFlight.registratie,
  );

  const hasSleep =
    flight && ["sleep", "tmg-a", "sep-a"].includes(flight.start_methode);

  const gezagVoerder = selectElementByID(
    vliegers,
    "id",
    flight && flight.gezagvoerder_id,
  );
  const tweedeInzittende = selectElementByID(
    vliegers,
    "id",
    flight && flight.tweede_inzittende_id,
  );
  const sleepVlieger = selectElementByID(
    vliegers,
    "id",
    sleepFlight && sleepFlight.gezagvoerder_id,
  );

  const readOnly =
    readOnlyDay || (flight && (flight.is_deleted || flight.is_locked));

  const [flightModalVisible, setFlightModal] = useState(false);

  const saveFlight = async (data) => {
    // saving full element only if there is no ID
    const result = await updateFlight(
      flight.id ? { ...data, uuid: flight.uuid } : { ...flight, ...data },
    );

    // double check on save flight if the betalend lid id/voucher_id is the same
    const betalend_lid_id = data.betalend_lid_id || flight.betalend_lid_id;
    const betalend_lid_2_id =
      ("betalend_lid_2_id" in data
        ? data.betalend_lid_2_id
        : flight.betalend_lid_2_id) || "DELETE";
    const voucher_id = data.voucher_id || flight.voucher_id;
    if (
      sleepFlight &&
      (sleepFlight.betalend_lid_id !== betalend_lid_id ||
        (sleepFlight.betalend_lid_2_id || "DELETE") !== betalend_lid_2_id ||
        sleepFlight.voucher_id !== voucher_id)
    ) {
      await updateFlight({
        uuid: sleepFlight.uuid,
        betalend_lid_id: betalend_lid_id,
        betalend_lid_2_id: betalend_lid_2_id,
        voucher_id: voucher_id,
      });
    }

    setFlightModal(false);
    return result;
  };

  const saveSleepFlight = async (data) => {
    // function to save data, e.g. times, but only if there is a sleepVlucht (makes call easier)
    if (sleepFlight) {
      return await updateFlight({ ...data, uuid: sleepFlight.uuid });
    }
  };

  return (
    <div className="selectedFlight">
      <FlightDetailsModal
        kist={selectedKist}
        vliegers={vliegers}
        visible={flightModalVisible}
        onClose={() => setFlightModal(false)}
        saveFlight={saveFlight}
        saveSleepFlight={saveSleepFlight}
        flight={flight}
        sleepFlight={sleepFlight}
        readOnly={readOnly}
      />
      <div
        className="row content"
        style={{ alignItems: "flex-start", marginTop: 6 }}
      >
        <div className="column section">
          <p className="small gray" style={{ marginBottom: 3 }}>
            {$t("Kist")}:
          </p>
          <SectionKist
            isSleep={false}
            selectedKist={selectedKist}
            kisten={kisten}
            flight={flight}
            saveFlight={saveFlight}
            readOnly={readOnly || (flight.start_tijd && flight.start_ogn > 0)}
            is_today={activeDay.is_today}
            width={undefined}
            selectDefaultPilot={true}
            vliegers={vliegers}
          />

          <SectionStartmethode
            extra_methods={
              selectedKist?.category == "sleep" ? ["tmg-a", "sep-a"] : []
            }
            maxWidth={350}
            flight={flight}
            saveFlight={saveFlight}
            readOnly={
              readOnly ||
              (flight && flight.start_methode === "sleep" && flight.sleep_uuid)
            }
            size={hasSleep ? "small" : "medium"}
          />

          {hasSleep && (
            <div>
              <p className="small gray" style={{ marginBottom: 3 }}>
                {$t("Sleepkist")} (
                {sleepFlight ? $t(sleepFlight.start_methode) : "-"}):
              </p>

              <div
                className="column"
                style={{ gap: 12, marginBottom: 6, alignItems: "flex-start" }}
              >
                {sleepFlight && (
                  <SectionKist
                    isSleep={true}
                    selectedKist={selectedSleepKist}
                    kisten={sleep_kisten}
                    flight={sleepFlight}
                    saveFlight={saveSleepFlight}
                    readOnly={
                      readOnly ||
                      (sleepFlight.start_tijd && sleepFlight.start_ogn > 0)
                    }
                    is_today={sleepFlight?.is_today}
                    width={undefined}
                  />
                )}

                <SleepActions
                  flight={flight}
                  saveFlight={saveFlight}
                  allFlights={allFlights}
                  sleepFlight={sleepFlight}
                  readOnly={readOnly}
                  disconnectSleepFlight={disconnectSleepFlight}
                  connectSleepFlight={connectSleepFlight}
                  addNewSleepFlight={addNewSleepFlight}
                  sleepKisten={sleep_kisten}
                  dayActiveSleepKist={dayActiveSleepKist}
                />
              </div>
            </div>
          )}
        </div>

        <div
          className="section column"
          style={{ gap: 20 }}
        >
          <SelectPilot
            showWarning={true}
            dag_id={activeDay.dag_id}
            readOnly={readOnly || !flight.registratie}
            field_id="gezagvoerder_id"
            field_name="gezagvoerder_naam"
            title={
              selectedKist && selectedKist.pilots === 2
                ? $t("PIC/instructeur")
                : $t("PIC")
            }
            pilot={gezagVoerder}
            otherPilot={tweedeInzittende}
            gezagVoerder={gezagVoerder}
            vliegers={aanmeldingen}
            alle_vliegers={vliegers}
            flight={flight}
            saveFlight={saveFlight}
            openPilot={() => setSelectedPilot(gezagVoerder)}
            hideAvatar={false}
            kist_in_dto={selectedKist?.in_dto}
            enable_authorization={enable_authorization}
            showFinance={showFinance}
            addUnknown={club?.default_vliegers !== '--'}
          />

          {(!selectedKist ||
            selectedKist.pilots === 2 ||
            flight.tweede_inzittende_naam) && (
            <Button
              disabled={!flight.tweede_inzittende_id || !flight.gezagvoerder_id}
              shape="circle"
              icon={<SwapOutlined />}
              size="small"
              type="text"
              style={{ position: "absolute", top: 70, left: -6 }}
              onClick={() => {
                // determine if tweede is instructeur, in which case it is a fis flight
                saveFlight({
                  betalend_lid_id: flight.gezagvoerder_id,
                  betalend_lid_2_id: "DELETE",
                  gezagvoerder_id: flight.tweede_inzittende_id,
                  gezagvoerder_naam: flight.tweede_inzittende_naam,
                  tweede_inzittende_id: flight.gezagvoerder_id,
                  tweede_inzittende_naam: flight.gezagvoerder_naam,
                  is_fis:
                    selectedKist?.in_dto &&
                    tweedeInzittende &&
                    tweedeInzittende.is_instructeur
                      ? true
                      : false,
                  is_training: false,
                  is_examen: false,
                  is_profcheck: false,
                });
              }}
            />
          )}

          {(!selectedKist ||
            selectedKist.pilots === 2 ||
            flight.tweede_inzittende_naam) && (
            <SelectPilot
              showWarning={true}
              dag_id={activeDay.dag_id}
              readOnly={readOnly || !flight.registratie}
              field_id="tweede_inzittende_id"
              field_name="tweede_inzittende_naam"
              title={$t("Pax/DBO/2e")}
              pilot={tweedeInzittende}
              otherPilot={gezagVoerder}
              gezagVoerder={gezagVoerder}
              vliegers={aanmeldingen}
              alle_vliegers={vliegers}
              flight={flight}
              saveFlight={saveFlight}
              openPilot={() => setSelectedPilot(tweedeInzittende)}
              hideAvatar={false}
              kist_in_dto={selectedKist?.in_dto}
              showFinance={showFinance}
              addUnknown={club?.default_vliegers !== '--'}
            />
          )}

          {sleepFlight && (
            <div className="row">
              <p className="capitalize">{$t("sleepvlieger")}:</p>
              <SelectPilot
                dag_id={activeDay.dag_id}
                hideAvatar={true}
                readOnly={readOnly}
                field_id="gezagvoerder_id"
                field_name="gezagvoerder_naam"
                title={$t("sleepvlieger")}
                pilot={sleepVlieger}
                otherPilot={null}
                gezagVoerder={null}
                vliegers={aanmeldingen.filter(
                  (aanmelding) =>
                    aanmelding.id > 0 && aanmelding.as_sleepvlieger,
                )}
                alle_vliegers={vliegers}
                flight={sleepFlight}
                saveFlight={saveSleepFlight}
                openPilot={() => setSelectedPilot(sleepVlieger)}
                kist_in_dto={false}
                addUnknown={club?.default_vliegers !== '--'}
              />
            </div>
          )}
        </div>

        <div className="section" style={{ maxWidth: 200 }}>
          <SectionChecks
            readOnly={readOnly || !flight.registratie}
            editInstructorDetails={null}
            flight={flight}
            saveFlight={saveFlight}
            sleepFlight={sleepFlight}
            saveSleepFlight={saveSleepFlight}
            gezagvoerder={gezagVoerder}
            tweedeInzittende={tweedeInzittende}
          />
        </div>

        <div className="column section buttons">
          <SectionButtons
            readOnly={readOnly}
            sleepFlight={sleepFlight}
            flight={flight}
            isPending={isPending}
            saveFlight={saveFlight}
            saveSleepFlight={saveSleepFlight}
            showFlightEditModal={() => setFlightModal(true)}
          />
        </div>
      </div>
    </div>
  );
};
